import { useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowDown } from "lucide-react";
import { Slider } from "../ui/slider";
import { CountUp } from "../CountUp";
import { Reveal, SectionTag } from "../Reveal";
import { CurrencyInput } from "./CurrencyInput";
import { SavingsTimeline } from "./SavingsTimeline";
import { CostComparison } from "./CostComparison";
import { fmtMoney, fmtRate } from "../../lib/format";
import { trackEvent } from "../../lib/analytics";
import { RATE_FLOOR_PCT, scrollToForm } from "../../config";

const SLIDER_MIN = 5000;
const SLIDER_MAX = 1000000;

export const SavingsCalculator = ({ onBook, volume, expense, onVolumeChange, onExpenseChange }) => {
  const fired = useRef({});

  const fireOnce = (key, event, params = {}) => {
    if (fired.current[key]) return;
    fired.current[key] = true;
    trackEvent(event, params);
  };

  const handleVolume = (v) => {
    onVolumeChange(v);
    fireOnce("started", "calculator_started");
    fireOnce("vol", "monthly_volume_entered", { monthly_volume: v });
  };
  const handleExpense = (v) => {
    onExpenseChange(v);
    fireOnce("started", "calculator_started");
    fireOnce("exp", "monthly_expense_entered", { monthly_expense: v });
  };

  const error = useMemo(() => {
    if (volume != null && volume <= 0) return "Monthly volume must be greater than $0.";
    if (expense != null && expense <= 0) return "Monthly processing expense must be greater than $0.";
    if (volume != null && expense != null && expense > volume)
      return "Processing expense can't exceed your monthly volume — double-check your statement.";
    return null;
  }, [volume, expense]);

  const ready = volume > 0 && expense > 0 && expense <= volume;

  const calc = useMemo(() => {
    if (!ready) return null;
    const currentRateDecimal = expense / volume;
    // 25% reduction, floored at estimated minimum interchange cost and never above the current rate
    const floorDecimal = RATE_FLOOR_PCT / 100;
    const projectedRateDecimal = Math.min(
      currentRateDecimal,
      Math.max(currentRateDecimal * 0.75, floorDecimal)
    );
    const projectedExpense = volume * projectedRateDecimal;
    const monthlySavings = Math.max(0, expense - projectedExpense);
    return {
      currentRatePct: currentRateDecimal * 100,
      projectedRatePct: projectedRateDecimal * 100,
      reductionPct: Math.round(((currentRateDecimal - projectedRateDecimal) / currentRateDecimal) * 100),
      floorApplied: currentRateDecimal * 0.75 < floorDecimal,
      noSavings: monthlySavings <= 0,
      projectedExpense,
      monthlySavings,
      six: monthlySavings * 6,
      one: monthlySavings * 12,
      two: monthlySavings * 24,
      three: monthlySavings * 36,
      currentThreeYear: expense * 36,
      projectedThreeYear: projectedExpense * 36,
      highRate: currentRateDecimal * 100 > 3.5,
    };
  }, [ready, volume, expense]);

  useEffect(() => {
    if (calc) {
      fireOnce("calc", "calculation_completed", { monthly_volume: volume, monthly_expense: expense });
      fireOnce("rate", "effective_rate_calculated", { rate: Number(calc.currentRatePct.toFixed(2)) });
    }
  }, [calc, volume, expense]);

  const sliderValue = Math.min(Math.max(volume ?? SLIDER_MIN, SLIDER_MIN), SLIDER_MAX);

  return (
    <section id="calculator" data-testid="savings-calculator" className="relative mx-auto max-w-7xl scroll-mt-24 px-6 py-24 sm:py-32">
      <SectionTag index="06" label="Illustrative Savings Calculator" center />
      <Reveal className="mx-auto mt-6 max-w-3xl text-center">
        <h2 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl">
          See How Much You <span className="text-mint">Could Save</span>
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-white/60 sm:text-lg">
          Find out what your current credit card processing is really costing you — and see how much
          you could potentially save with CardConnect Commerce.
        </p>
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.3em] text-mint">
          It takes less than 30 seconds
        </p>
      </Reveal>

      <div className="mt-16 grid gap-6 lg:grid-cols-2">
        {/* INPUTS */}
        <Reveal>
          <div className="h-full rounded-3xl border border-ink/10 bg-surface shadow-xl shadow-ink/5 p-8 backdrop-blur-2xl sm:p-10">
            <CurrencyInput
              label="How much do you process in credit cards each month?"
              value={volume}
              onChange={handleVolume}
              placeholder="100,000"
              testid="calc-volume-input"
              error={volume != null && volume <= 0 ? "Volume must be greater than $0." : null}
            />
            <div className="mt-6">
              <Slider
                data-testid="calc-volume-slider"
                min={SLIDER_MIN}
                max={SLIDER_MAX}
                step={1000}
                value={[sliderValue]}
                onValueChange={([v]) => handleVolume(v)}
                className="[&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&_[role=slider]]:border-mint [&_[role=slider]]:bg-void [&_.bg-primary]:bg-mint"
              />
              <div className="mt-2 flex justify-between font-mono text-[10px] tracking-widest text-white/40">
                <span>$5,000</span>
                <span>$1,000,000+</span>
              </div>
            </div>

            <div className="mt-10">
              <CurrencyInput
                label="How much are you currently paying in processing fees each month?"
                helper="You can usually find this on your latest merchant processing statement."
                value={expense}
                onChange={handleExpense}
                placeholder="4,000"
                testid="calc-expense-input"
                error={
                  expense != null && expense <= 0
                    ? "Expense must be greater than $0."
                    : error
                    ? error
                    : null
                }
              />
            </div>
            <button
              type="button"
              data-testid="calc-dont-know-fees"
              onClick={scrollToForm}
              className="mt-6 font-mono text-xs font-bold uppercase tracking-[0.2em] text-mint transition-transform duration-300 hover:translate-x-1"
            >
              I don't know my current fees →
            </button>
          </div>
        </Reveal>

        {/* RESULTS */}
        <Reveal delay={0.1}>
          <div className="h-full rounded-3xl border border-ink/10 bg-surface shadow-xl shadow-ink/5 p-8 shadow-[0_0_40px_rgba(0,156,222,0.08)] backdrop-blur-2xl sm:p-10">
            {!calc ? (
              <div className="flex h-full min-h-[320px] flex-col items-center justify-center text-center" data-testid="calc-empty-state">
                <p className="font-mono text-sm text-white/50">
                  Enter your monthly volume and processing expense to reveal your estimated savings.
                </p>
              </div>
            ) : (
              <motion.div
                onViewportEnter={() => fireOnce("viewed", "savings_result_viewed", { monthly_savings: Math.round(calc.monthlySavings) })}
                viewport={{ once: true }}
              >
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                      Your current effective rate
                    </p>
                    <CountUp
                      value={calc.currentRatePct}
                      format={fmtRate}
                      testid="current-rate"
                      className="mt-2 font-mono text-4xl font-bold text-white sm:text-5xl"
                    />
                  </div>
                  <motion.div
                    animate={{ y: [0, 6, 0] }}
                    transition={{ duration: 1.6, repeat: Infinity }}
                    className="text-mint"
                  >
                    <ArrowDown size={28} />
                  </motion.div>
                  <div className="text-right">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                      Illustrative rate (25% lower)
                    </p>
                    <CountUp
                      value={calc.projectedRatePct}
                      format={fmtRate}
                      testid="projected-rate"
                      className="mt-2 font-mono text-4xl font-bold text-mint sm:text-5xl"
                    />
                  </div>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-white/40">
                  Your effective rate represents the percentage of your credit card sales that is
                  currently going toward processing costs.
                </p>

                {!calc.noSavings && (
                  <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-mint px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-cream" data-testid="rate-badge">
                    {calc.reductionPct}% lower effective rate
                  </div>
                )}
                <p className="mt-4 w-fit rounded-full border border-amber-500/40 bg-amber-500/10 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-amber-400" data-testid="scenario-badge">
                  Illustrative scenario — assumes a 25% reduction
                </p>
                {calc.floorApplied && !calc.noSavings && (
                  <p className="mt-3 text-[11px] leading-relaxed text-white/40" data-testid="rate-floor-note">
                    Projected rate includes estimated minimum card network (interchange) costs —
                    exact pricing is confirmed in your statement review.
                  </p>
                )}

                {calc.highRate && (
                  <div className="mt-6 rounded-2xl border border-danger/40 bg-danger/10 p-5" data-testid="high-rate-warning">
                    <div className="flex items-center gap-2 font-display text-base font-bold text-danger">
                      <AlertTriangle size={18} />
                      Your Current Rate May Have Significant Room for Improvement
                    </div>
                    <p className="mt-2 text-sm text-white/60">
                      Based on your current effective rate, it may be worth having CardConnect
                      Commerce review your processing statement and identify potential savings.
                    </p>
                  </div>
                )}

                <div className="mt-8 border-t border-white/10 pt-8">
                  {calc.noSavings ? (
                    <div data-testid="no-savings-note">
                      <p className="font-display text-2xl font-extrabold tracking-tight text-mint">
                        Your rate already looks competitive.
                      </p>
                      <p className="mt-3 text-sm leading-relaxed text-white/60">
                        Based on these numbers, your effective rate is already near estimated
                        minimum network costs. A CardConnect specialist can still review your
                        statement for junk fees and confirm exact pricing.
                      </p>
                    </div>
                  ) : (
                    <>
                      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                        You could save an estimated
                      </p>
                      <div className="mt-3 flex flex-wrap items-baseline gap-x-3">
                        <CountUp
                          value={calc.monthlySavings}
                          format={fmtMoney}
                          testid="monthly-savings"
                          className="animate-mint-pulse font-mono text-6xl font-bold text-mint sm:text-7xl"
                        />
                        <span className="font-mono text-xl text-white/60">/ month</span>
                      </div>
                      <p className="mt-3 text-sm text-white/60">
                        That's{" "}
                        <CountUp
                          value={calc.one}
                          format={fmtMoney}
                          testid="annual-savings"
                          className="font-mono font-bold text-mint"
                        />{" "}
                        that could stay in your business every year.
                      </p>
                    </>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </Reveal>
      </div>

      {calc && (
        <>
          <SavingsTimeline calc={calc} />
          <CostComparison calc={calc} />

          <Reveal className="mx-auto mt-24 max-w-3xl text-center">
            <h3 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
              Want to See Exactly What CardConnect Commerce{" "}
              <span className="text-mint">Can Save You?</span>
            </h3>
            <p className="mx-auto mt-5 max-w-2xl text-white/60">
              Every business and processing statement is different. Your calculator results are an
              estimate based on your current costs. Let our payment experts review your current
              processing and show you exactly where you may be able to save.
            </p>
            <button
              data-testid="calc-book-cta"
              onClick={onBook}
              className="mt-8 rounded-full bg-mint px-10 py-5 font-display text-lg font-bold text-cream shadow-[0_0_50px_rgba(0,156,222,0.3)] transition-transform duration-300 hover:scale-105 hover:brightness-110"
            >
              Request My Free Savings Review →
            </button>
            <p className="mt-4 font-mono text-xs text-white/40">
              No obligation. No pressure. Just a clear breakdown of what you're paying today and
              what you could potentially save.
            </p>
          </Reveal>

          <Reveal className="mx-auto mt-16 max-w-4xl">
            <p data-testid="calc-disclaimer" className="text-center text-[11px] leading-relaxed text-white/35">
              Savings shown are estimates for illustrative purposes only and are based on the
              information provided and a projected 25% reduction in the calculated effective
              processing rate. Actual rates, fees, and savings vary based on processing volume, card
              mix, transaction types, interchange costs, equipment, services, and other factors.
              Projected rates are never shown below estimated minimum interchange costs set by the
              card networks. Calculator results are not a quote, offer, or guarantee of pricing or
              savings.
            </p>
          </Reveal>
        </>
      )}
    </section>
  );
};
